import { useState, useCallback } from "react";
import { FaTimes, FaMoneyBillWave } from "react-icons/fa";
import { postPaymentRefund } from "JS/API";

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  modal: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    width: "100%",
    maxWidth: "520px",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "17px",
    fontWeight: "600",
    color: "#1e293b",
    margin: 0,
  },
  closeButton: {
    padding: "8px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  body: {
    padding: "24px",
  },
  infoSection: {
    backgroundColor: "#f8fafc",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "20px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 0",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#64748b",
  },
  infoValue: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1e293b",
  },
  fieldGroup: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "6px",
  },
  radioGroup: {
    display: "flex",
    gap: "12px",
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "14px",
    color: "#1e293b",
    cursor: "pointer",
    padding: "8px 16px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    transition: "all 0.2s ease",
  },
  radioLabelActive: {
    borderColor: "#3b82f6",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1e293b",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1e293b",
    resize: "vertical",
    minHeight: "60px",
    boxSizing: "border-box",
  },
  accountSection: {
    backgroundColor: "#fff7ed",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
    border: "1px solid #fed7aa",
  },
  accountTitle: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#c2410c",
    marginBottom: "12px",
  },
  accountField: {
    marginBottom: "10px",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "16px 24px",
    borderTop: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
  },
  cancelBtn: {
    padding: "10px 20px",
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
  },
  refundBtn: {
    padding: "10px 20px",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
  },
  refundBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  errorText: {
    color: "#dc2626",
    fontSize: "12px",
    marginTop: "4px",
  },
  successResult: {
    textAlign: "center",
    padding: "20px",
  },
  successIcon: {
    fontSize: "48px",
    marginBottom: "12px",
  },
  successTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#166534",
    marginBottom: "8px",
  },
  successDetail: {
    fontSize: "14px",
    color: "#475569",
    lineHeight: "1.6",
  },
};

const RefundModal = ({ info, onClose, onSuccess }) => {
  const [refundType, setRefundType] = useState("FULL");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("관리자 환불 처리");
  const [accountInfo, setAccountInfo] = useState({
    bank: "",
    account_number: "",
    holder_name: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [confirmStep, setConfirmStep] = useState(false);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

  const paidAmount = info?.paid_amount || info?.merchant_price || 0;
  const balanceAmount = info?.balance_amount ?? paidAmount;
  const isVirtualAccount = info?.toss_info?.raw_response?.virtualAccount != null;
  const orderId = info?.order_id || info?.toss_info?.order_id;
  const paidMethod = info?.paid_method;

  const getRefundAmount = () => {
    if (refundType === "FULL") return balanceAmount;
    return Number(refundAmount) || 0;
  };

  const validate = () => {
    if (!orderId) return "결제 정보(order_id)를 찾을 수 없습니다.";
    if (refundType === "PARTIAL") {
      const amount = Number(refundAmount);
      if (!amount || amount <= 0) return "환불 금액을 입력해주세요.";
      if (amount > balanceAmount)
        return `환불 가능 금액(${balanceAmount.toLocaleString()}원)을 초과했습니다.`;
    }
    if (isVirtualAccount) {
      if (!accountInfo.bank) return "환불 은행을 입력해주세요.";
      if (!accountInfo.account_number) return "환불 계좌번호를 입력해주세요.";
      if (!accountInfo.holder_name) return "예금주를 입력해주세요.";
    }
    return null;
  };

  const handleConfirm = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setConfirmStep(true);
  };

  const handleRefund = useCallback(async () => {
    setIsLoading(true);
    setError("");

    const body = {
      order_id: orderId,
      refund_type: refundType,
      refund_reason: refundReason || "관리자 환불 처리",
    };

    if (refundType === "PARTIAL") {
      body.refund_amount = Number(refundAmount);
    }

    if (isVirtualAccount) {
      body.refund_receive_account = accountInfo;
    }

    try {
      const res = await postPaymentRefund(body);
      if (typeof res === "object" && res?.data) {
        setResult(res.data);
        onSuccess?.();
      } else {
        setError(typeof res === "string" ? res : "환불 처리에 실패했습니다.");
        setConfirmStep(false);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "환불 처리 중 오류가 발생했습니다.";
      setError(detail);
      setConfirmStep(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    orderId,
    refundType,
    refundAmount,
    refundReason,
    isVirtualAccount,
    accountInfo,
    onSuccess,
  ]);

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) setMouseDownOnOverlay(true);
  };

  const handleOverlayMouseUp = (e) => {
    if (e.target === e.currentTarget && mouseDownOnOverlay) onClose();
    setMouseDownOnOverlay(false);
  };

  // 환불 완료 화면
  if (result) {
    return (
      <div
        style={styles.overlay}
        onMouseDown={handleOverlayMouseDown}
        onMouseUp={handleOverlayMouseUp}
      >
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h3 style={styles.headerTitle}>
              <FaMoneyBillWave /> 환불 완료
            </h3>
            <button style={styles.closeButton} onClick={onClose}>
              <FaTimes size={18} />
            </button>
          </div>
          <div style={styles.body}>
            <div style={styles.successResult}>
              <div style={styles.successIcon}>✅</div>
              <div style={styles.successTitle}>{result.message}</div>
              <div style={styles.successDetail}>
                <p>
                  환불 방식:{" "}
                  {result.refund_method === "TOSS"
                    ? "토스 자동 환불"
                    : "수동 계좌이체 필요"}
                </p>
                <p>
                  환불 유형:{" "}
                  {result.refund_type === "FULL" ? "전액 환불" : "부분 환불"}
                </p>
                <p>환불 금액: {result.refund_amount?.toLocaleString()}원</p>
                {result.refundable_amount != null && (
                  <p>
                    남은 환불 가능 금액:{" "}
                    {result.refundable_amount?.toLocaleString()}원
                  </p>
                )}
                {result.refund_method === "BANK_MANUAL" && (
                  <p
                    style={{
                      color: "#c2410c",
                      fontWeight: "600",
                      marginTop: "8px",
                    }}
                  >
                    ※ 무통장 결제 건으로, 실제 계좌 이체는 수동으로 진행해주세요.
                  </p>
                )}
              </div>
            </div>
          </div>
          <div style={styles.footer}>
            <button style={styles.cancelBtn} onClick={onClose}>
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 확인 단계
  if (confirmStep) {
    return (
      <div
        style={styles.overlay}
        onMouseDown={handleOverlayMouseDown}
        onMouseUp={handleOverlayMouseUp}
      >
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h3 style={styles.headerTitle}>
              <FaMoneyBillWave /> 환불 확인
            </h3>
          </div>
          <div style={styles.body}>
            <div style={styles.infoSection}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>환불 유형</span>
                <span style={styles.infoValue}>
                  {refundType === "FULL" ? "전액 환불" : "부분 환불"}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>환불 금액</span>
                <span
                  style={{ ...styles.infoValue, color: "#dc2626" }}
                >
                  {getRefundAmount().toLocaleString()}원
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>환불 사유</span>
                <span style={styles.infoValue}>
                  {refundReason || "관리자 환불 처리"}
                </span>
              </div>
            </div>
            <p
              style={{
                textAlign: "center",
                fontSize: "15px",
                fontWeight: "600",
                color: "#dc2626",
              }}
            >
              환불을 진행하시겠습니까?
              <br />
              <span style={{ fontSize: "13px", fontWeight: "400", color: "#64748b" }}>
                {paidMethod === "toss"
                  ? "토스를 통해 즉시 환불 처리됩니다."
                  : "무통장 입금 건으로, DB 이력만 기록됩니다. 실제 이체는 수동으로 진행해주세요."}
              </span>
            </p>
            {error && <p style={styles.errorText}>{error}</p>}
          </div>
          <div style={styles.footer}>
            <button
              style={styles.cancelBtn}
              onClick={() => setConfirmStep(false)}
              disabled={isLoading}
            >
              뒤로
            </button>
            <button
              style={{
                ...styles.refundBtn,
                ...(isLoading ? styles.refundBtnDisabled : {}),
              }}
              onClick={handleRefund}
              disabled={isLoading}
            >
              {isLoading ? "처리 중..." : "환불 실행"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 입력 화면
  return (
    <div
      style={styles.overlay}
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
    >
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.headerTitle}>
            <FaMoneyBillWave /> 환불 처리
          </h3>
          <button style={styles.closeButton} onClick={onClose}>
            <FaTimes size={18} />
          </button>
        </div>

        <div style={styles.body}>
          {/* 결제 정보 요약 */}
          <div style={styles.infoSection}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>결제 금액</span>
              <span style={styles.infoValue}>
                {paidAmount.toLocaleString()}원
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>환불 가능 금액</span>
              <span style={{ ...styles.infoValue, color: "#2563eb" }}>
                {balanceAmount.toLocaleString()}원
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>결제 수단</span>
              <span style={styles.infoValue}>
                {paidMethod === "toss"
                  ? `토스 - ${info?.toss_info?.method || ""}`
                  : paidMethod === "bank"
                  ? "무통장입금"
                  : paidMethod || "-"}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>결제 상태</span>
              <span style={styles.infoValue}>
                {info?.paid_status === "paid"
                  ? "결제완료"
                  : info?.paid_status === "partial_refund"
                  ? "부분 환불"
                  : info?.paid_status || "-"}
              </span>
            </div>
            {paidMethod === "bank" && (
              <div style={{ ...styles.infoRow, borderTop: "1px solid #e2e8f0", paddingTop: "10px", marginTop: "4px" }}>
                <span style={styles.infoLabel}>환불 계좌</span>
                {info?.refund_account_info ? (
                  <span style={{ ...styles.infoValue, color: "#059669" }}>
                    {info.refund_account_info.refund_bank_name}{" "}
                    {info.refund_account_info.refund_account}{" "}
                    ({info.refund_account_info.refund_holder})
                  </span>
                ) : (
                  <span style={{ ...styles.infoValue, color: "#dc2626" }}>
                    등록된 환불 계좌 없음
                  </span>
                )}
              </div>
            )}
          </div>
          {paidMethod === "bank" && (
            <div style={{
              padding: "10px 12px",
              borderRadius: "8px",
              fontSize: "12px",
              marginBottom: "16px",
              backgroundColor: info?.refund_account_info ? "#f0fdf4" : "#fef2f2",
              color: info?.refund_account_info ? "#166534" : "#991b1b",
              border: `1px solid ${info?.refund_account_info ? "#bbf7d0" : "#fecaca"}`,
            }}>
              {info?.refund_account_info
                ? "※ 무통장 입금 건입니다. 위 계좌로 수동 송금 후 환불 처리해주세요."
                : "※ 무통장 입금 건이나 환불 계좌가 미등록 상태입니다. 고객에게 환불 계좌를 확인해주세요."}
            </div>
          )}

          {/* 환불 유형 */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>환불 유형</label>
            <div style={styles.radioGroup}>
              <label
                style={{
                  ...styles.radioLabel,
                  ...(refundType === "FULL" ? styles.radioLabelActive : {}),
                }}
                onClick={() => setRefundType("FULL")}
              >
                <input
                  type="radio"
                  name="refundType"
                  checked={refundType === "FULL"}
                  onChange={() => setRefundType("FULL")}
                />
                전액 환불 ({balanceAmount.toLocaleString()}원)
              </label>
              <label
                style={{
                  ...styles.radioLabel,
                  ...(refundType === "PARTIAL" ? styles.radioLabelActive : {}),
                }}
                onClick={() => setRefundType("PARTIAL")}
              >
                <input
                  type="radio"
                  name="refundType"
                  checked={refundType === "PARTIAL"}
                  onChange={() => setRefundType("PARTIAL")}
                />
                부분 환불
              </label>
            </div>
          </div>

          {/* 부분 환불 금액 */}
          {refundType === "PARTIAL" && (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                환불 금액 (최대 {balanceAmount.toLocaleString()}원)
              </label>
              <input
                type="number"
                style={styles.input}
                placeholder="환불 금액을 입력하세요"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                max={balanceAmount}
                min={1}
              />
            </div>
          )}

          {/* 환불 사유 */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>환불 사유</label>
            <textarea
              style={styles.textarea}
              placeholder="환불 사유를 입력하세요"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* 가상계좌 환불 계좌 정보 */}
          {isVirtualAccount && (
            <div style={styles.accountSection}>
              <div style={styles.accountTitle}>
                가상계좌 결제 건 - 환불 계좌 정보 입력
              </div>
              <div style={styles.accountField}>
                <label style={styles.label}>은행명</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="예: 국민은행"
                  value={accountInfo.bank}
                  onChange={(e) =>
                    setAccountInfo((prev) => ({
                      ...prev,
                      bank: e.target.value,
                    }))
                  }
                />
              </div>
              <div style={styles.accountField}>
                <label style={styles.label}>계좌번호</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="계좌번호 입력"
                  value={accountInfo.account_number}
                  onChange={(e) =>
                    setAccountInfo((prev) => ({
                      ...prev,
                      account_number: e.target.value,
                    }))
                  }
                />
              </div>
              <div style={styles.accountField}>
                <label style={styles.label}>예금주</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="예금주 입력"
                  value={accountInfo.holder_name}
                  onChange={(e) =>
                    setAccountInfo((prev) => ({
                      ...prev,
                      holder_name: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          )}

          {error && <p style={styles.errorText}>{error}</p>}
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>
            취소
          </button>
          <button style={styles.refundBtn} onClick={handleConfirm}>
            환불 진행
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundModal;